import {
  AlertCircle,
  Archive,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  Edit2,
  RotateCcw,
  UserCheck,
} from "lucide-react";
import React, { useState } from "react";
import { TODAY } from "@/data/financial-systems";
import type { ActionAuditEntry } from "@/types/actions";

const fmtDateTime = (d: Date) =>
  d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

type AuditEventType = ActionAuditEntry["eventType"];

interface FlatEntry extends ActionAuditEntry {
  actionTitle: string;
  assignedToName: string;
}

// Standalone demo data — matches SEED_ACTIONS in ActionsContext
// (In live mode this report is driven by the backend API)
const NOW = TODAY;
const daysAgo = (n: number) =>
  new Date(NOW.getTime() - n * 24 * 60 * 60 * 1000);
const hoursAgo = (h: number) => new Date(NOW.getTime() - h * 60 * 60 * 1000);

const DEMO_ENTRIES: FlatEntry[] = [
  // act-001
  {
    id: "ae-001-1",
    actionId: "act-001",
    actionTitle: "Chase overdue invoice — Mersey Build Group",
    assignedToName: "Edna Foster",
    actorId: "6",
    actorName: "Sarah Mitchell",
    eventType: "created",
    detail: "Action created",
    timestamp: daysAgo(7),
  },
  {
    id: "ae-001-2",
    actionId: "act-001",
    actionTitle: "Chase overdue invoice — Mersey Build Group",
    assignedToName: "Edna Foster",
    actorId: "3",
    actorName: "Edna Foster",
    eventType: "updated",
    detail: "First reminder email sent to accounts@merseygroup.co.uk",
    timestamp: daysAgo(5),
  },
  {
    id: "ae-001-3",
    actionId: "act-001",
    actionTitle: "Chase overdue invoice — Mersey Build Group",
    assignedToName: "Edna Foster",
    actorId: "3",
    actorName: "Edna Foster",
    eventType: "completed",
    detail: "Partial payment received (£12,400). Remaining balance chased.",
    timestamp: daysAgo(1),
  },

  // act-002
  {
    id: "ae-002-1",
    actionId: "act-002",
    actionTitle: "Review Q1 payroll reconciliation",
    assignedToName: "Sarah Mitchell",
    actorId: "4",
    actorName: "Linda Kowalski",
    eventType: "created",
    detail: "Action created",
    timestamp: daysAgo(3),
  },

  // act-003
  {
    id: "ae-003-1",
    actionId: "act-003",
    actionTitle: "Confirm equipment hire renewal — JCB backhoe",
    assignedToName: "Sarah Mitchell",
    actorId: "6",
    actorName: "Sarah Mitchell",
    eventType: "created",
    detail: "Action created",
    timestamp: daysAgo(5),
  },
  {
    id: "ae-003-2",
    actionId: "act-003",
    actionTitle: "Confirm equipment hire renewal — JCB backhoe",
    assignedToName: "Sarah Mitchell",
    actorId: "6",
    actorName: "Sarah Mitchell",
    eventType: "reassigned",
    detail:
      "Reassigned to Edna Foster — taking over supplier contact management",
    timestamp: daysAgo(2),
  },

  // act-004
  {
    id: "ae-004-1",
    actionId: "act-004",
    actionTitle: "Update cash flow model for BigCo revised payment terms",
    assignedToName: "Sarah Mitchell",
    actorId: "4",
    actorName: "Linda Kowalski",
    eventType: "created",
    detail: "Action created",
    timestamp: daysAgo(2),
  },
  {
    id: "ae-004-2",
    actionId: "act-004",
    actionTitle: "Update cash flow model for BigCo revised payment terms",
    assignedToName: "Sarah Mitchell",
    actorId: "6",
    actorName: "Sarah Mitchell",
    eventType: "completed",
    detail:
      "Model updated. March low point now £148K on 45-day first milestone.",
    timestamp: hoursAgo(4),
  },

  // act-005
  {
    id: "ae-005-1",
    actionId: "act-005",
    actionTitle: "Prepare board pack — cash section",
    assignedToName: "Linda Kowalski",
    actorId: "1",
    actorName: "Catherine Hughes",
    eventType: "created",
    detail: "Action created",
    timestamp: daysAgo(4),
  },

  // act-010 (archived)
  {
    id: "ae-010-1",
    actionId: "act-010",
    actionTitle: "Review supplier payment terms — Keystone Materials",
    assignedToName: "Sarah Mitchell",
    actorId: "4",
    actorName: "Linda Kowalski",
    eventType: "created",
    detail: "Action created",
    timestamp: daysAgo(20),
  },
  {
    id: "ae-010-2",
    actionId: "act-010",
    actionTitle: "Review supplier payment terms — Keystone Materials",
    assignedToName: "Sarah Mitchell",
    actorId: "4",
    actorName: "Linda Kowalski",
    eventType: "archived",
    detail: "Archived by Linda Kowalski",
    timestamp: daysAgo(5),
  },
];

const eventConfig: Record<
  AuditEventType,
  { label: string; icon: React.ReactNode; colour: string }
> = {
  created: {
    label: "Created",
    icon: <CheckSquare className="h-3.5 w-3.5" />,
    colour:
      "text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
  },
  updated: {
    label: "Updated",
    icon: <Edit2 className="h-3.5 w-3.5" />,
    colour:
      "text-slate-600 bg-slate-50 border-slate-200 dark:bg-slate-950/30 dark:border-slate-800",
  },
  completed: {
    label: "Completed",
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    colour:
      "text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800",
  },
  reopened: {
    label: "Reopened",
    icon: <RotateCcw className="h-3.5 w-3.5" />,
    colour:
      "text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
  },
  archived: {
    label: "Archived",
    icon: <Archive className="h-3.5 w-3.5" />,
    colour:
      "text-slate-600 bg-slate-50 border-slate-200 dark:bg-slate-950/30 dark:border-slate-800",
  },
  reinstated: {
    label: "Reinstated",
    icon: <RotateCcw className="h-3.5 w-3.5" />,
    colour:
      "text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
  },
  reassigned: {
    label: "Reassigned",
    icon: <UserCheck className="h-3.5 w-3.5" />,
    colour:
      "text-violet-600 bg-violet-50 border-violet-200 dark:bg-violet-950/30 dark:border-violet-800",
  },
};

const ALL_EVENT_TYPES: AuditEventType[] = [
  "created",
  "updated",
  "completed",
  "reopened",
  "archived",
  "reinstated",
  "reassigned",
];

export const ActionsWorkflowAuditPreview: React.FC = () => {
  const [filter, setFilter] = useState<AuditEventType | "all">("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const reportDate = TODAY.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const sorted = [...DEMO_ENTRIES].sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
  );
  const filtered =
    filter === "all" ? sorted : sorted.filter((e) => e.eventType === filter);

  const counts = ALL_EVENT_TYPES.reduce<Record<string, number>>((acc, evt) => {
    acc[evt] = DEMO_ENTRIES.filter((e) => e.eventType === evt).length;
    return acc;
  }, {});

  const uniqueActions = Array.from(
    new Set(DEMO_ENTRIES.map((e) => e.actionId)),
  ).length;

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="rounded-lg border bg-primary/5 border-primary/20 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">Actions Workflow Audit</h3>
          <p className="text-sm text-muted-foreground">
            BuildRight Construction Ltd · All actions · Lifecycle and assignment
            events
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Report Date</p>
          <p className="font-semibold">{reportDate}</p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-5 gap-2">
        {(
          [
            "created",
            "completed",
            "reassigned",
            "reopened",
            "archived",
          ] as AuditEventType[]
        ).map((evt) => {
          const cfg = eventConfig[evt];
          return (
            <button
              key={evt}
              onClick={() => setFilter(filter === evt ? "all" : evt)}
              className={`rounded-lg border p-3 text-center transition-all hover:opacity-80 ${filter === evt ? cfg.colour : "bg-muted/40 border-muted"}`}
            >
              <div
                className={`flex justify-center mb-1 ${filter === evt ? "" : "text-muted-foreground"}`}
              >
                {cfg.icon}
              </div>
              <p className="text-lg font-bold">{counts[evt] ?? 0}</p>
              <p className="text-xs text-muted-foreground leading-tight mt-0.5">
                {cfg.label}
              </p>
            </button>
          );
        })}
      </div>

      {filter !== "all" && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filtered by:</span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${eventConfig[filter].colour}`}
          >
            {eventConfig[filter].icon}
            {eventConfig[filter].label}
          </span>
          <button
            onClick={() => setFilter("all")}
            className="text-xs text-muted-foreground underline hover:no-underline"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                Date / Time
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                Action
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                Actor
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">
                Event
              </th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map((entry) => {
              const cfg = eventConfig[entry.eventType];
              const isExpanded = expandedId === entry.id;
              return (
                <React.Fragment key={entry.id}>
                  <tr
                    className={`hover:bg-muted/30 transition-colors ${isExpanded ? "bg-muted/30" : ""}`}
                  >
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">
                      {fmtDateTime(entry.timestamp)}
                    </td>
                    <td className="px-4 py-2.5 font-medium max-w-[220px] truncate">
                      {entry.actionTitle}
                    </td>
                    <td className="px-4 py-2.5">{entry.actorName}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.colour}`}
                      >
                        {cfg.icon}
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {entry.detail && (
                        <button
                          onClick={() =>
                            setExpandedId(isExpanded ? null : entry.id)
                          }
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                          Detail
                          <ChevronDown
                            className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                          />
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && entry.detail && (
                    <tr className="bg-muted/20">
                      <td
                        colSpan={5}
                        className="px-4 py-2.5 text-sm text-muted-foreground italic border-l-2 border-primary/30"
                      >
                        {entry.detail}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                >
                  No audit entries found for the selected filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Live mode note */}
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3">
        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-700 dark:text-amber-400">
          In live mode, this report is driven by the backend audit trail and
          covers all actions across your full dataset. The preview above uses
          demo data.
        </p>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-4">
        <CheckSquare className="h-3.5 w-3.5" />
        <span>
          {DEMO_ENTRIES.length} total audit events across {uniqueActions}{" "}
          actions
        </span>
      </div>
    </div>
  );
};
